import React, { useEffect, useState } from 'react'
import { Image, Divider, Typography } from 'antd'
import { ErrorNotification } from 'services/helpers/helpers'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import { getContent } from 'services/ContentfulApi'

export const useContentful = (contentfulUrl: string, contentfulToken: string, language: string) => {
  const [pages, setPages] = useState<any[]>([])
  const [assets, setAssets] = useState<any[]>([])
  const [contacts, setContacts] = useState<any[] | null>(null)
  const [posts, setPosts] = useState<any[]>([])
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(false)
  const [isContentfulLoading, setIsContentfulLoading] = useState<boolean>(true)
  const { Title } = Typography

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pageResult = await getContent(contentfulUrl, contentfulToken, 'page', language)
        const contactResult = await getContent(
          contentfulUrl,
          contentfulToken,
          'contactUs',
          language
        )
        const postResult = await getContent(contentfulUrl, contentfulToken, 'newsPost', language)
        setAssets(pageResult.includes?.Asset)
        setPages(pageResult.items)
        setPosts(postResult.items)
        setContacts(contactResult.items)
      } catch (error) {
        ErrorNotification(
          'Could not load content',
          'Some content is not accessible. Try again later'
        )
      } finally {
        setIsContentfulLoading(false)
      }
    }

    fetchData()
  }, [contentfulUrl, contentfulToken, language])

  const renderAsset = (node) => {
    const currentAsset = assets.find((asset) => asset.sys.id === node.data.target.sys.id)
    if (!currentAsset) return null

    const { file, title } = currentAsset.fields || {}
    const { contentType, url } = file || {}

    if (contentType?.startsWith('image/')) {
      return <Image src={url} alt={title || 'Embedded Image'} />
    }

    if (contentType?.startsWith('video/')) {
      return (
        <video
          controls
          style={{
            maxWidth: '100%',
            maxHeight: '250px',
            objectFit: 'contain',
          }}
        >
          <source src={url} type={contentType} />
        </video>
      )
    }

    return null
  }

  const renderHeading = (level, node) => {
    const HeadingComponent = Title
    return <HeadingComponent level={level}>{node.content[0].value}</HeadingComponent>
  }

  const nodeRenderingOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: renderAsset,
      [BLOCKS.HEADING_1]: (node) => renderHeading(1, node),
      [BLOCKS.HEADING_2]: (node) => renderHeading(2, node),
      [BLOCKS.HEADING_3]: (node) => renderHeading(3, node),
      [BLOCKS.HEADING_4]: (node) => renderHeading(4, node),
      [BLOCKS.HR]: () => <Divider />,
    },
  }

  return {
    pages,
    posts,
    contacts,
    isContentfulLoading,
    isAuthLoading,
    setIsAuthLoading,
    documentToReactComponents,
    nodeRenderingOptions,
  }
}
